import { Controller } from "@hotwired/stimulus";

/**
 * Pulsing Border Controller
 *
 * Creates an animated pulsing border effect using the Paper Design shader library.
 * Supports dark mode with automatic color switching.
 */
export default class extends Controller<HTMLElement> {
  private shaderMount: any = null;
  private isInitialized: boolean = false;
  private themeObserver: MutationObserver | null = null;
  private mediaQuery: MediaQueryList | null = null;
  private systemThemeListener: ((e: MediaQueryListEvent) => void) | null = null;
  private getShaderColorFromString: ((color: string) => number[]) | null = null;

  // Light mode colors - match navbar bg so colors "appear" from it
  private lightColors = [
    "#e2e0d9", // base-100 light (navbar bg in light mode)
    "#e2e0d9", // same
    "#c5f0a4", // neon green accent
  ];

  // Dark mode colors - match navbar bg so colors "appear" from it
  private darkColors = [
    "#2e2b27", // base-100 dark (navbar bg in dark mode)
    "#2e2b27", // same
    "#c5f0a4", // neon green accent
  ];

  async connect(): Promise<void> {
    await this.initShader();
    this.setupThemeObserver();
    this.setupSystemThemeListener();
  }

  disconnect(): void {
    if (this.shaderMount) {
      if (typeof this.shaderMount.dispose === "function") {
        this.shaderMount.dispose();
      }
    }
    if (this.themeObserver) {
      this.themeObserver.disconnect();
    }
    this.removeSystemThemeListener();
  }

  private isDarkMode(): boolean {
    // First check the data-theme attribute (set by theme controller)
    const html = document.documentElement;
    const dataTheme = html.getAttribute("data-theme");
    if (dataTheme) {
      return dataTheme === "lumenfall-dark";
    }

    // Fall back to localStorage preference (same key as theme controller)
    const savedTheme = localStorage.getItem("lumenTheme");
    if (savedTheme === "dark") {
      return true;
    }
    if (savedTheme === "light") {
      return false;
    }

    // Fall back to system preference (for "system" setting or no setting)
    return window.matchMedia?.("(prefers-color-scheme: dark)").matches ?? false;
  }

  private getColors(): string[] {
    return this.isDarkMode() ? this.darkColors : this.lightColors;
  }

  private setupThemeObserver(): void {
    this.themeObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.attributeName === "data-theme") {
          this.updateColors();
        }
      }
    });

    this.themeObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });
  }

  private setupSystemThemeListener(): void {
    if (!window.matchMedia) return;

    this.mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    this.systemThemeListener = () => {
      // Only react if user has "system" theme or no preference set
      const savedTheme = localStorage.getItem("lumenTheme");
      if (!savedTheme || savedTheme === "system") {
        this.updateColors();
      }
    };

    this.mediaQuery.addEventListener("change", this.systemThemeListener);
  }

  private removeSystemThemeListener(): void {
    if (this.mediaQuery && this.systemThemeListener) {
      this.mediaQuery.removeEventListener("change", this.systemThemeListener);
    }
  }

  private updateColors(): void {
    if (!this.shaderMount || !this.getShaderColorFromString) return;

    const colorStrings = this.getColors();
    const colors = colorStrings.map((c) => this.getShaderColorFromString!(c));

    this.shaderMount.setUniforms({
      u_colors: colors,
    });
  }

  private async initShader(): Promise<void> {
    try {
      const {
        ShaderMount,
        pulsingBorderFragmentShader,
        getShaderColorFromString,
        getShaderNoiseTexture,
        PulsingBorderAspectRatios,
      } = await import("@paper-design/shaders");

      // Store for later use in updateColors
      this.getShaderColorFromString = getShaderColorFromString;

      const colorStrings = this.getColors();
      const colors = colorStrings.map((c) => getShaderColorFromString(c));

      const noiseTexture = getShaderNoiseTexture();
      if (noiseTexture && !noiseTexture.complete) {
        await new Promise<void>((resolve) => {
          noiseTexture.onload = () => resolve();
          noiseTexture.onerror = () => resolve();
        });
      }

      this.shaderMount = new ShaderMount(
        this.element,
        pulsingBorderFragmentShader,
        {
          u_colors: colors,
          u_colorsCount: colors.length,
          u_colorBack: [0, 0, 0, 0], // Fully transparent background
          u_roundness: 0.25,
          u_thickness: 0.08,
          u_softness: 0.5,
          u_aspectRatio: PulsingBorderAspectRatios.auto,
          u_intensity: 0.5,
          u_bloom: 0,
          u_spots: 4,
          u_spotSize: 0.5,
          u_pulse: 0.25,
          u_smoke: 0,
          u_smokeSize: 0,
          u_scale: 1,
          u_marginLeft: 0,
          u_marginRight: 0,
          u_marginTop: 0,
          u_marginBottom: 0,
          u_noiseTexture: noiseTexture,
        },
        { alpha: true, premultipliedAlpha: true },
        1 // Animation speed
      );

      this.isInitialized = true;
    } catch (error) {
      console.error("Failed to initialize pulsing border shader:", error);
    }
  }
}
